import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Badge, Button, Spinner } from 'react-bootstrap';
import { Eye, GitCompare, History, RotateCcw, X } from 'lucide-react';
import { apiErrorMessage, marketingSiteService } from '../../services/apiService';
import { useAuthToken } from '../../hooks/useAuthToken';
import { firstLine, shortSha, timeAgo } from './ui';

/** Whether a commit has reached production ("live") or is still only on the test site. */
const LiveBadge = ({ live }) => {
  if (live === true) return <Badge bg="success">In production</Badge>;
  if (live === false) return <Badge bg="warning">Test site only</Badge>;
  return null;
};

/**
 * The page's commits on main, newest first. The newest is the version being edited; any older one
 * can be viewed over the canvas, compared with the current page, or restored as a new commit.
 */
const HistoryPanel = ({ page, canEdit, dirty, viewingRef, reloadToken, onView, onCompare, onRestore, onClose }) => {
  const ensureToken = useAuthToken();
  const [history, setHistory] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      await ensureToken();
      setHistory((await marketingSiteService.pageHistory(page.path)).data);
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not load the history.'));
    }
  }, [ensureToken, page.path]);

  useEffect(() => {
    setHistory(null);
    load();
  }, [load, reloadToken]);

  const commits = history?.commits || [];

  return (
    <aside className="mse-panel mse-history">
      <div className="mse-history-head">
        <History size={14} />
        <strong>History</strong>
        <span className="mse-hint">{page.url}</span>
        <button type="button" className="mse-link-button ms-auto" onClick={onClose} aria-label="Close history">
          <X size={16} />
        </button>
      </div>
      <div className="mse-panel-body">
        {error && <Alert variant="danger">{error}</Alert>}
        {!history && !error && (
          <div className="mse-empty">
            <Spinner animation="border" size="sm" /> Loading history
          </div>
        )}
        {history && commits.length === 0 && <div className="mse-empty">No commits found for this page.</div>}
        {dirty && canEdit && commits.length > 1 && (
          <p className="mse-hint">Save or discard your edits before restoring a version. Viewing is fine.</p>
        )}
        <ol className="mse-history-list">
          {commits.map((c, i) => {
            const isCurrent = i === 0;
            return (
              <li key={c.sha} className={`mse-history-item${viewingRef === c.sha ? ' active' : ''}`}>
                <div className="mse-history-title">
                  <a href={c.url} target="_blank" rel="noopener noreferrer">
                    <code>{shortSha(c.sha)}</code>
                  </a>
                  <span className="mse-history-message" title={c.message}>
                    {firstLine(c.message)}
                  </span>
                </div>
                <div className="mse-history-meta">
                  {c.authorName} · {timeAgo(c.date)} <LiveBadge live={c.live} />
                  {isCurrent && <Badge bg="secondary">Current</Badge>}
                </div>
                {!isCurrent && (
                  <div className="mse-history-actions">
                    <button type="button" className="mse-link-button" onClick={() => onView(c)}>
                      <Eye size={12} /> View
                    </button>
                    <button type="button" className="mse-link-button" onClick={() => onCompare(c)}>
                      <GitCompare size={12} /> Compare
                    </button>
                    {canEdit && (
                      <button
                        type="button"
                        className="mse-link-button"
                        onClick={() => onRestore(c)}
                        disabled={dirty}
                        title={dirty ? 'Save or discard your edits first' : 'Restore this version as a new commit'}
                      >
                        <RotateCcw size={12} /> Restore
                      </button>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ol>
        {history && !history.liveKnown && commits.length > 0 && (
          <p className="mse-hint">Production status is unknown: production has commits that the test site does not.</p>
        )}
      </div>
    </aside>
  );
};

export const VersionBanner = ({ entry, onBack, onCompare, onRestore, canRestore }) => (
  <div className="mse-version-banner">
    <Eye size={14} />
    <span className="mse-version-text">
      Viewing <code>{shortSha(entry.sha)}</code> from {timeAgo(entry.date)} by {entry.authorName}: read only.
    </span>
    <span className="mse-toolbar-spacer" />
    <Button size="sm" variant="outline-secondary" onClick={onCompare}>
      Compare
    </Button>
    {canRestore && (
      <Button size="sm" variant="outline-secondary" onClick={onRestore}>
        Restore
      </Button>
    )}
    <Button size="sm" variant="primary" onClick={onBack}>
      Back to current
    </Button>
  </div>
);

export default HistoryPanel;
